import { useSheet } from '@/contexts/SheetContext'
import { useDownload } from '@/hooks/useDownload'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Text,
} from '@flatfile/baseline'
import { useTranslation } from 'react-i18next'
import { useCallback, memo, useMemo } from 'react'

// Inner component that only renders when sheet data is available
const SheetHeaderActionsInner = memo(
  ({ sheetId, fileId }: { sheetId: string; fileId: string }) => {
    const { t } = useTranslation()
    const { handleDownload, isDownloading } = useDownload(sheetId, fileId)

    const exportToCSV = useCallback(() => {
      handleDownload('csv')
    }, [handleDownload])

    const exportToJSON = useCallback(() => {
      handleDownload('json')
    }, [handleDownload])

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            loading={isDownloading}
            icon='chevron-bottom'
            iconPlacement='right'
          >
            {t('files.buttons.download')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuLabel className='text-muted'>
            {t('files.buttons.download')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={exportToCSV}>
            <div>
              <div className='flex items-center gap-1 mb-1'>
                <Icon name='file-text' />
                <span>CSV</span>
              </div>
              <Text variant='footnote' color='muted'>
                {t('files.downloadsTable.csvDescription')}
              </Text>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={exportToJSON}>
            <div>
              <div className='flex items-center gap-1'>
                <Icon name='brackets-2' />
                <span>JSON</span>
              </div>
              <Text variant='footnote' color='muted'>
                {t('files.downloadsTable.jsonDescription')}
              </Text>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)

SheetHeaderActionsInner.displayName = 'SheetHeaderActionsInner'

// Wrapper component that conditionally renders the inner component only when sheet data is available
export const SheetHeaderActions = memo(() => {
  const { sheet, isLoading } = useSheet()
  const { t } = useTranslation()

  // Only render the download button when we have a valid sheet
  const shouldRender = useMemo(
    () => !isLoading && sheet && sheet.id && sheet.fileId,
    [isLoading, sheet]
  )

  if (!shouldRender) {
    return (
      <Button disabled icon='chevron-bottom' iconPlacement='right'>
        {t('files.buttons.download')}
      </Button>
    )
  }

  return <SheetHeaderActionsInner sheetId={sheet.id} fileId={sheet.fileId} />
})
